import { cn } from '@plato/ui/util/classes';
import type React from 'react';

/**
 * Shared layout for the breakdown hints on the Team table (Activity, Insight
 * engagement, AI impact). A two-column grid (label | value) sized to its content
 * so long labels and multi-part values never wrap, and all values align.
 *
 * Hosts must pass `max-w-none` to `InfoHintContent`: the tooltip's default `max-w-xs`
 * clips this grid instead of wrapping it.
 *
 * Colours are drawn from `currentColor`: an `InfoHint` is a dark popup on desktop
 * and a light sheet on a phone, so a fixed palette would be unreadable in one of them.
 * Below `sm` the sheet body dims everything to `content-weaker`; the grid resets to
 * full-strength `content` so emphasized rows read at normal colour and the rest step
 * down through the `/70` alpha.
 */
export function InfoHintStatGrid({ children }: React.PropsWithChildren) {
    return (
        <div className="grid w-full grid-cols-[1fr_auto] items-baseline gap-x-8 gap-y-1.5 text-left text-content sm:w-max sm:text-current">
            {children}
        </div>
    );
}

export function InfoHintStatDivider() {
    return <div className="col-span-2 my-1 border-t border-current/20" />;
}

type InfoHintStatRowProps = {
    label: React.ReactNode;
    value: React.ReactNode;
    emphasized?: boolean;
    indented?: boolean;
};

export function InfoHintStatRow({ label, value, emphasized, indented }: InfoHintStatRowProps) {
    return (
        <>
            <span className={cn('whitespace-nowrap', !emphasized && 'text-current/70', indented && 'pl-3')}>
                {label}
            </span>
            <span
                className={cn('whitespace-nowrap text-right tabular-nums', !emphasized && 'text-current/70')}
            >
                {value}
            </span>
        </>
    );
}
